import * as React from 'react';
import {
  AddedInstrument,
  AddedInstruments,
  InstrumentArraySchema,
  ProfileSkillLevelsArraySchema,
} from '../types';
import { InstrumentsEventKeys, InstrumentsEvents } from '../events';
import { EventBus } from '@/shared/utils';
import { useAuthStore } from '@/auth';

export const useInstrumentsForm = ({
  instruments,
  profileSkillLevels,
  eventBus,
}: {
  instruments: InstrumentArraySchema;
  profileSkillLevels: ProfileSkillLevelsArraySchema;
  eventBus: EventBus<InstrumentsEvents>;
}) => {
  const { user } = useAuthStore();

  const [addedInstruments, setAddedInstruments] = React.useState<AddedInstruments>([]);
  const [notAddedInstruments, setNotAddedInstruments] = React.useState<AddedInstruments>([]);
  const [removing, setRemoving] = React.useState<string | null>(null);
  const [selectedInstrument, setSelectedInstrument] = React.useState<AddedInstrument | null>(null);
  const [selectedSkillLevel, setSelectedSkillLevel] = React.useState<string | undefined>();
  const [deletingSkillLevel, setDeletingSkillLevel] = React.useState<{
    instrument: AddedInstrument;
    skillLevel?: string;
  } | null>();
  const [isUpdating, setIsUpdating] = React.useState(false);

  const handleOpenDialog = React.useCallback(
    (instrument: AddedInstrument, { isDeleting = false, isUpdating = false } = {}) => {
      if (isDeleting && instrument.skillLevel) {
        setDeletingSkillLevel({ instrument, skillLevel: instrument.skillLevel });
        return;
      }
      if (isUpdating) {
        setIsUpdating(true);
        if (instrument.skillLevel) setSelectedSkillLevel(instrument.skillLevel);
      }
      setSelectedInstrument(instrument);
    },
    [],
  );

  const handleCloseDialog = React.useCallback(() => {
    setSelectedInstrument(null);
    if (selectedSkillLevel) setSelectedSkillLevel(undefined);
    if (deletingSkillLevel) setDeletingSkillLevel(null);
    if (isUpdating) setIsUpdating(false);
  }, [selectedSkillLevel, deletingSkillLevel, isUpdating]);

  const handleInstrument = React.useCallback(
    (selectedOption: string | undefined, isUpdating = false, resetForm: () => void) => {
      if (!selectedInstrument || !selectedOption) return;
      if (isUpdating) {
        eventBus.emit(InstrumentsEventKeys.instruments.skillLevels.update, {
          data: {
            instrument: selectedInstrument.name,
            skillLevel: selectedOption,
          },
          onSuccess: () => {
            handleCloseDialog();
            resetForm();
            // we refetch the profile skills after adding a new skill level to invalidete the cache
            if (user?.email)
              eventBus.emit(InstrumentsEventKeys.instruments.profile, { email: user.email });
          },
        });
        return;
      }
      eventBus.emit(InstrumentsEventKeys.instruments.skillLevels.create, {
        data: {
          instrument: selectedInstrument?.name,
          skillLevel: selectedOption,
        },
        onSuccess: () => {
          if (user?.email) {
            // we refetch the profile skills after adding a new skill level to invalidete the cache
            eventBus.emit(InstrumentsEventKeys.instruments.profile, { email: user.email });
          }
          resetForm();
          handleCloseDialog();
        },
      });
    },
    [eventBus, selectedInstrument, handleCloseDialog, user?.email],
  );

  const handleDeleteInstrument = React.useCallback(() => {
    if (!deletingSkillLevel) return;
    eventBus.emit(InstrumentsEventKeys.instruments.skillLevels.delete, {
      data: {
        instrument: deletingSkillLevel.instrument.name,
      },
      onSuccess: () => {
        const id = deletingSkillLevel.instrument.name;
        setRemoving(id); // Trigger the exit animation
        setTimeout(() => {
          setAddedInstruments((prevInstruments) =>
            prevInstruments.filter((instrument) => instrument.name !== id),
          );
          setNotAddedInstruments((prevInstruments) => [
            ...prevInstruments,
            {
              name: deletingSkillLevel.instrument.name,
              imageSrc: deletingSkillLevel.instrument.imageSrc,
            },
          ]);
          if (user?.email)
            // we refetch the profile skills after adding a new skill level to invalidete the cache
            eventBus.emit(InstrumentsEventKeys.instruments.profile, { email: user.email });
          setRemoving(null);
        }, 500);
        handleCloseDialog();
      },
    });
  }, [deletingSkillLevel, eventBus, handleCloseDialog, user?.email]);

  React.useEffect(() => {
    const added = profileSkillLevels.map((psl) => ({
      name: psl.instrument.instrumentName,
      // TODO remove this fallback when we'll have all the icons
      imageSrc: psl.instrument.icon?.url || '',
      skillLevel: psl.skillLevel.name,
    }));
    setAddedInstruments(added);
    const parentInstruments = instruments.reduce<string[]>(
      (acc, curr) =>
        curr.parentInstrument?.instrumentName
          ? [...acc, curr.parentInstrument.instrumentName]
          : acc,
      [],
    );
    const notAdded = instruments
      .filter((i) => {
        const instrumentsName = profileSkillLevels.map((f) => f.instrument.instrumentName);
        return (
          !instrumentsName.includes(i.instrumentName) &&
          !parentInstruments.find((el) => el === i.instrumentName)
        );
      })
      .map((i) => ({
        name: i.instrumentName,
        // TODO remove this fallback when we'll have all the icons
        imageSrc: i.icon?.url || '',
      }));

    setNotAddedInstruments(notAdded);
  }, [profileSkillLevels, instruments]);

  return {
    addedInstruments,
    notAddedInstruments,
    removing,
    selectedInstrument,
    selectedSkillLevel,
    deletingSkillLevel,
    handleOpenDialog,
    handleCloseDialog,
    handleInstrument,
    handleDeleteInstrument,
    isUpdating,
  };
};
